import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from '../CtaButton';
import { PageLightbox } from './PageLightbox';
import { useSnapCarousel } from '../../hooks/useSnapCarousel';
import { volumeFivePages } from '../../data/volumeFivePages';
export function VolumePreview() {
  const count = volumeFivePages.length;
  const {
    trackRef,
    setSlideRef,
    active,
    scrollTo
  } = useSnapCarousel(count);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const onTrackKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollTo(Math.min(count - 1, active + 1));
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scrollTo(Math.max(0, active - 1));
    }
  };
  const changeLightbox = (i: number) => {
    setLightboxIndex(i);
    scrollTo(i);
  };
  return <section aria-labelledby="vol5-heading" className="w-full overflow-hidden bg-v5-cream py-14 font-v5sans text-v5-ink sm:py-20" data-id="element-337" data-mp="364diza8ntw98">
      <div className="mx-auto max-w-[1200px] px-5 text-center" data-id="element-338" data-mp="2icvzfoh3s0v5">
        <p className="text-[12px] font-bold uppercase tracking-[2px] text-v5-coral" data-id="element-339" data-mp="2dawuia5psvw3">
          Look inside · Volume 5
        </p>
        <h2 id="vol5-heading" className="mt-3 font-v5display text-[32px] leading-[1.1] text-v5-plum sm:text-5xl" data-id="element-340" data-mp="2s6wbybeps8dw">
          See exactly what you’ll run on Monday night
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-v5-ink sm:text-lg" data-id="element-341" data-mp="1zhyta76lc9km">
          Every plan is a two-page spread: the arc on one page, the cues on the
          next. Here are 10 real pages from Presence &amp; Nature, one of six
          volumes.
        </p>
      </div>

      <div className="relative mx-auto mt-10 max-w-[1200px] [container-type:inline-size]" data-id="element-342" data-mp="24hjuypd5aw11">
        <svg aria-hidden="true" viewBox="0 0 1200 120" preserveAspectRatio="none" className="pointer-events-none absolute left-0 top-[38%] h-24 w-full opacity-25" data-id="element-343" data-mp="1grpgk3d6gzeo">
          <defs data-id="element-344" data-mp="25kgc91ww53o7">
            <linearGradient id="vol5-wave" x1="0" x2="1" y1="0" y2="0" data-id="element-345" data-mp="25k7vv01k7g89">
              <stop offset="0%" stopColor="#E8735A" data-id="element-346" data-mp="25i3k5vxf5cxf" />
              <stop offset="50%" stopColor="#8FAF8B" data-id="element-347" data-mp="2ac7vsshmq94u" />
              <stop offset="100%" stopColor="#4FA3A5" data-id="element-348" data-mp="2fa9lvxdh38pt" />
            </linearGradient>
          </defs>
          <path d="M-10 70 C 110 18, 210 112, 330 62 S 540 14, 660 66 S 870 118, 990 58 S 1150 22, 1210 54" fill="none" stroke="url(#vol5-wave)" strokeWidth="3" strokeLinecap="round" data-id="element-349" data-mp="1ik0dzoqe0qov" />
        </svg>

        <div ref={trackRef} tabIndex={0} role="region" aria-roledescription="carousel" aria-label="Pages from Volume 5 · Presence & Nature. Use arrow keys to browse." onKeyDown={onTrackKeyDown} className="vol5-track relative flex overflow-x-auto pb-2 pt-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-v5-coral/60" data-id="element-350" data-mp="1rjdomkq8zlb6">
          {volumeFivePages.map((page, i) => {
          const caption = `${page.label}: ${page.line}`;
          return <figure key={page.src} ref={setSlideRef(i)} className="vol5-slide m-0" aria-roledescription="slide" aria-label={`${i + 1} of ${count}`} data-id="element-351" data-mp="mligojiyg9ze" data-mpi={i}>
                <button type="button" onClick={() => setLightboxIndex(i)} aria-label={`Open full page: ${page.label}`} className="relative block w-full overflow-hidden rounded-2xl bg-white shadow-[0_12px_32px_rgba(75,46,79,0.12)] transition-transform duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-v5-coral active:scale-[0.99]" data-id="element-352" data-mp="dmonl34u21ql">
                  <img src={page.src} alt={caption} width={1080} height={1920} loading={i === 0 ? 'eager' : 'lazy'} draggable={false} className="aspect-[9/16] w-full object-cover" data-id="element-353" data-mp="1t23y7pcbh0rp" />
                  <span className="absolute left-3 top-3 rounded-full bg-white px-2.5 py-1 text-[12px] font-semibold text-v5-plum shadow-sm" data-id="element-354" data-mp="1r3kof61rse18">
                    Vol. 5 · p. {page.page}
                  </span>
                </button>
                <figcaption className="mt-3 px-1 text-left text-[15px] leading-snug text-v5-ink" data-id="element-355" data-mp="1ia7nbgtjy642">
                  <span className="font-bold" data-id="element-356" data-mp="1n4lnrxn2n3fl">{page.label}:</span> {page.line}
                </figcaption>
              </figure>;
        })}
        </div>

        <button type="button" onClick={() => scrollTo(active - 1)} disabled={active === 0} aria-label="Previous page" className="absolute left-3 top-[calc(50%-36px)] z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-v5-plum shadow-[0_6px_18px_rgba(75,46,79,0.18)] transition-opacity duration-150 ease-out hover:bg-v5-cream disabled:pointer-events-none disabled:opacity-0 lg:flex" data-id="element-357" data-mp="1ddh80vpknejp">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-358" data-mp="ts8kmnx61fib" />
        </button>
        <button type="button" onClick={() => scrollTo(active + 1)} disabled={active === count - 1} aria-label="Next page" className="absolute right-3 top-[calc(50%-36px)] z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-v5-plum shadow-[0_6px_18px_rgba(75,46,79,0.18)] transition-opacity duration-150 ease-out hover:bg-v5-cream disabled:pointer-events-none disabled:opacity-0 lg:flex" data-id="element-359" data-mp="ortb67g08s5m">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-360" data-mp="1mfbhzcq3vk6z" />
        </button>
      </div>

      <ol className="mt-6 flex justify-center" aria-label="Choose a page" data-id="element-361" data-mp="1hha0gw8bvsxm">
        {volumeFivePages.map((page, i) => <li key={page.src} data-id="element-362" data-mp="f4dhqyzkhopx" data-mpi={i}>
            <button type="button" onClick={() => scrollTo(i)} aria-label={`Go to page ${i + 1}: ${page.label}`} aria-current={i === active ? 'true' : undefined} className="flex h-6 w-6 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-v5-coral rounded-full" data-id="element-363" data-mp="f56pa26coi94">
              <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === active ? 'w-5 bg-v5-coral' : 'w-2 bg-v5-plum/20'}`} data-id="element-364" data-mp="xt1yr9uxj9wf" />
            </button>
          </li>)}
      </ol>

      <div className="mx-auto mt-8 flex max-w-md flex-col items-center px-5 text-center" data-id="element-365" data-mp="1bsbfcan1s60x">
        <p className="text-base text-v5-ink" data-id="element-366" data-mp="38my1g4ltt47v">
          That’s 1 of 6 volumes. 84 plans in total, all built the same way.
        </p>
        <div className="mt-5 w-full" data-id="element-367" data-mp="3dn28l40ugmlf">
          <CtaButton data-id="element-368" data-mp="361yosqep0e6j">Get Vivencia Ready</CtaButton>
        </div>
      </div>

      <PageLightbox pages={volumeFivePages} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onChange={changeLightbox} data-id="element-369" data-mp="3639uwwvm1y0t" />
    </section>;
}